import api from './api';

export interface SamsungDevice {
  id: number;
  external_id: string;
  nombre: string;
  device_kind: 'ac' | 'tv';
  habitacion_id: number | null;
  artefacto_id: number | null;
  artefacto?: { nombre?: string; icono?: string; color_icono?: string } | null;
  capabilities?: string[];
  online: boolean;
  health_status?: string | null;
  power?: boolean | null;
  temperature?: number | null;
  setpoint?: number | null;
  hvac_mode?: string | null;
  fan_mode?: string | null;
  swing_mode?: string | null;
  volume?: number | null;
  mute?: boolean | null;
  input_source?: string | null;
  enabled?: boolean;
  missing_from_cloud?: boolean;
  orden?: number;
  pin_required?: boolean;
  control_mode?: 'cloud' | 'lan' | null;
  modelo?: string | null;
  lan_host?: string | null;
  lan_paired?: boolean;
  can_control?: boolean;
  can_view?: boolean;
}

export async function fetchSamsungAccount(): Promise<any> {
  const res = await api.get('/samsung/account');
  return res.data;
}

export async function samsungOAuthLogin(): Promise<{ auth_url: string }> {
  const res = await api.get('/samsung/oauth/login');
  return res.data;
}

export async function samsungConnectToken(access_token: string, refresh_token?: string): Promise<any> {
  const res = await api.post('/samsung/account/connect-token', { access_token, refresh_token });
  return res.data;
}

export async function samsungDisconnectAccount(): Promise<void> {
  await api.delete('/samsung/account');
}

export async function samsungPurgeAccount(): Promise<void> {
  await api.delete('/samsung/account/credentials');
}

export async function samsungSync(): Promise<any> {
  const res = await api.post('/samsung/sync');
  return res.data;
}

export async function fetchSamsungAdminSummary(): Promise<{ total: number; unassigned_count: number }> {
  const res = await api.get('/samsung/devices/admin-summary');
  return res.data;
}

export async function fetchSamsungDevices(): Promise<SamsungDevice[]> {
  const res = await api.get('/samsung/devices');
  return res.data || [];
}

export async function patchSamsungDevice(id: number, body: Record<string, unknown>): Promise<SamsungDevice> {
  const res = await api.patch(`/samsung/devices/${id}`, body);
  return res.data;
}

export async function deleteSamsungDevice(id: number): Promise<void> {
  await api.delete(`/samsung/devices/${id}`);
}

export async function reorderSamsungDevices(items: { id: number; orden: number }[]): Promise<void> {
  await api.put('/samsung/devices/reorder', { items });
}

export async function samsungControl(device_id: number, action: string, value?: unknown): Promise<any> {
  const res = await api.post('/samsung/control', { device_id, action, value });
  return res.data;
}

/** Relee estado real desde SmartThings (1 llamada al presupuesto). */
export async function samsungRefreshDevice(device_id: number): Promise<SamsungDevice> {
  const res = await api.post(`/samsung/devices/${device_id}/refresh`);
  return res.data;
}

/** Renueva TTL de foco sin llamar a SmartThings (solo TVs en backend). */
export async function samsungTouchFocus(deviceIds: number[]): Promise<void> {
  if (!deviceIds.length) return;
  await api.post('/samsung/focus', { device_ids: deviceIds });
}

export interface SamsungActivityPoint {
  time_bucket: string;
  activity_ratio: number;
  temperatura_promedio: number | null;
  setpoint_promedio?: number | null;
}

/** Serie histórica actividad (+ temperatura en AC) para el modal. */
export async function fetchSamsungActivitySeries(params: {
  deviceId: number;
  startDate: string;
  endDate: string;
  interval: string;
}): Promise<{ ok: boolean; device_kind: string; data: SamsungActivityPoint[] }> {
  const res = await api.get('/samsung/historical/activity-time-series', {
    params: {
      device_id: params.deviceId,
      start_date: params.startDate,
      end_date: params.endDate,
      interval: params.interval,
    },
  });
  return res.data;
}

export async function samsungSetTvMode(id: number, mode: 'cloud' | 'lan'): Promise<SamsungDevice> {
  const res = await api.post(`/samsung/tv/${id}/mode`, { mode });
  return res.data;
}

export async function samsungDiscoverTvs(): Promise<{ host: string; name?: string; model?: string | null; mac?: string | null }[]> {
  const res = await api.post('/samsung/tv/discover');
  return res.data?.devices || [];
}

export async function samsungPairTv(id: number, host: string, mac?: string | null): Promise<SamsungDevice> {
  const res = await api.post(`/samsung/tv/${id}/pair`, { host, mac: mac || null });
  return res.data;
}
